import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Trash2, Plus, X, GripVertical } from 'lucide-react';
import { generateChoiceId } from '../../../../src/schemas/templateContent';

interface ItemEditorProps {
  item: any;
  onChange: (item: any) => void;
  onDelete?: () => void;
}

export function ItemEditor({ item, onChange, onDelete }: ItemEditorProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateItem = (updates: any) => {
    onChange({ ...item, ...updates });
  };

  const updateScore = (scoreUpdates: any) => {
    const currentScore = item.score || {
      enabled: false,
      weight: 1,
      map: { Yes: 1, No: 0, 'N/A': 'ignore' }
    };
    
    updateItem({
      score: { ...currentScore, ...scoreUpdates }
    });
  };

  const renderScoringEditor = () => {
    if (item.type === 'section') {
      return (
        <div className="text-sm text-muted-foreground italic">
          Section headers are not scored
        </div>
      );
    }

    if (item.type === 'dropdown' || item.type !== 'ynna_actions_review') {
      return (
        <div className="text-sm text-muted-foreground italic">
          Scoring not available for this item type
        </div>
      );
    }

    const score = item.score || {
      enabled: true,
      weight: 1,
      map: { Yes: 1, No: 0, 'N/A': 'ignore' }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Enable Scoring</Label>
          <Switch
            checked={score.enabled}
            onCheckedChange={(enabled) => updateScore({ enabled })}
            data-testid="switch-scoring-enabled"
          />
        </div>

        {score.enabled && (
          <>
            <div>
              <Label>Weight</Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={score.weight}
                onChange={(e) => updateScore({ weight: parseFloat(e.target.value) || 1 })}
                className="mt-1"
                data-testid="input-score-weight"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Higher weights make this item more important in the overall score
              </p>
            </div>

            <div>
              <Label>Response Scoring</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {['Yes', 'No', 'N/A'].map((response) => (
                  <div key={response} className="space-y-2">
                    <Label className="text-xs">{response}</Label>
                    <div className="flex space-x-2">
                      <Button
                        variant={score.map[response] === 'ignore' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateScore({ 
                          map: { ...score.map, [response]: 'ignore' } 
                        })}
                        data-testid={`button-${response.toLowerCase()}-ignore`}
                      >
                        Ignore
                      </Button>
                      <Input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={typeof score.map[response] === 'number' ? score.map[response] : ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value)) {
                            updateScore({ 
                              map: { ...score.map, [response]: value } 
                            });
                          }
                        }}
                        className="w-20"
                        placeholder="0-1"
                        data-testid={`input-${response.toLowerCase()}-score`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Set scoring values (0-1) or choose "Ignore" to exclude from score calculation
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  const getItemTypeLabel = (type: string) => {
    const labels = {
      section: 'Section Header',
      ynna_actions_review: 'Y/N/N/A + Actions',
      dropdown: 'Dropdown',
      text: 'Text Input',
      textarea: 'Text Area',
      number: 'Number Input'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getItemTypeBadgeColor = (type: string) => {
    const colors = {
      section: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      ynna_actions_review: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      dropdown: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      text: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      textarea: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
      number: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  };

  return (
    <Card className="border-2">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CardTitle className="text-lg">Edit Item</CardTitle>
            <Badge className={getItemTypeBadgeColor(item.type)}>
              {getItemTypeLabel(item.type)}
            </Badge>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
              data-testid="button-toggle-advanced"
            >
              <Settings className="w-4 h-4" />
            </Button>
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="text-destructive hover:text-destructive"
                data-testid="button-delete-item"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Basic Settings */}
        <div>
          <Label>Item Label</Label>
          <Input
            value={item.label}
            onChange={(e) => updateItem({ label: e.target.value })}
            placeholder="Enter item label"
            className="mt-1"
            data-testid="input-item-label"
          />
          <p className="text-xs text-muted-foreground mt-1">
            This will be displayed as the question or section title
          </p>
        </div>

        {/* Item Type Selector - Available for ALL items */}
        <div>
          <Label>Item Type</Label>
          <Select
            value={item.type}
            onValueChange={(value: string) => {
              const updates: any = {};
              
              // Handle dropdown type
              if (value === 'dropdown') {
                updates.type = 'dropdown';
                updates.itemType = undefined; // Clean up deprecated field
                if (!item.config?.choices) {
                  updates.config = {
                    choices: [
                      { id: generateChoiceId(), label: 'Option 1', value: 'option_1' },
                      { id: generateChoiceId(), label: 'Option 2', value: 'option_2' }
                    ]
                  };
                }
                updates.actions_enabled = true;
                updates.score = undefined; // No scoring for dropdowns
              }
              // Handle ynna_actions_review type
              else if (value === 'ynna_actions_review') {
                updates.type = 'ynna_actions_review';
                updates.itemType = undefined; // Clean up deprecated field
                updates.config = undefined; // Remove dropdown config
                updates.actions_enabled = true;
                updates.score = {
                  enabled: true,
                  weight: 1,
                  map: { Yes: 1, No: 0, 'N/A': 'ignore' as const }
                };
              }
              // Handle other types (text, textarea, number, section)
              else {
                updates.type = value;
                updates.itemType = undefined; // Remove itemType for all types
                updates.config = undefined; // Remove config
                updates.actions_enabled = value !== 'section';
                updates.score = undefined; // Remove score for non-ynna types
              }
              
              updateItem(updates);
            }}
            data-testid="select-item-type"
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ynna_actions_review">Yes/No/N/A + Actions</SelectItem>
              <SelectItem value="dropdown">Dropdown</SelectItem>
              <SelectItem value="text">Text Input</SelectItem>
              <SelectItem value="textarea">Text Area</SelectItem>
              <SelectItem value="number">Number Input</SelectItem>
              <SelectItem value="section">Section Header</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            {item.type === 'dropdown'
              ? 'Users will select from custom dropdown options'
              : item.type === 'ynna_actions_review'
              ? 'Users will choose Yes, No, or N/A'
              : `${getItemTypeLabel(item.type)} field`}
          </p>
        </div>

        {/* Dropdown Configuration Panel */}
        {item.type === 'dropdown' && (
          <div className="space-y-3 p-3 bg-muted/50 rounded-md">
            <div className="flex items-center justify-between">
              <Label>Dropdown Options</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newChoice = {
                    id: generateChoiceId(),
                    label: `Option ${(item.config?.choices?.length || 0) + 1}`,
                    value: `option_${(item.config?.choices?.length || 0) + 1}`
                  };
                  updateItem({
                    config: {
                      ...item.config,
                      choices: [...(item.config?.choices || []), newChoice]
                    }
                  });
                }}
                data-testid="button-add-dropdown-option"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Option
              </Button>
            </div>

            <div className="space-y-2">
              {item.config?.choices?.map((choice: any, index: number) => (
                <div key={choice.id} className="flex items-center gap-2 bg-background p-2 rounded border">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <Input
                    value={choice.label}
                    onChange={(e) => {
                      const updatedChoices = [...(item.config?.choices || [])];
                      updatedChoices[index] = { ...choice, label: e.target.value };
                      updateItem({
                        config: { ...item.config, choices: updatedChoices }
                      });
                    }}
                    placeholder="Option label"
                    className="flex-1"
                    data-testid={`input-dropdown-option-${index}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const updatedChoices = item.config?.choices?.filter((_: any, i: number) => i !== index);
                      updateItem({
                        config: { ...item.config, choices: updatedChoices }
                      });
                    }}
                    disabled={(item.config?.choices?.length || 0) <= 2}
                    data-testid={`button-delete-option-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground">
              Minimum 2 options required. Users will select one option from this dropdown.
            </p>
          </div>
        )}

        {/* Required Toggle - Available for all field types except section */}
        {item.type !== 'section' && (
          <>
            <Separator />
            <div>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Required</Label>
                  <p className="text-xs text-muted-foreground">
                    User must provide an answer to this item
                  </p>
                </div>
                <Switch
                  checked={item.required || false}
                  onCheckedChange={(required) => updateItem({ required })}
                  data-testid="switch-required"
                />
              </div>
            </div>
          </>
        )}

        {/* Actions Toggle - Available for all field types except section */}
        {item.type !== 'section' && (
          <>
            <Separator />
            <div>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Enable Actions</Label>
                  <p className="text-xs text-muted-foreground">
                    Require manager review and completion for this item
                  </p>
                </div>
                <Switch
                  checked={item.actions_enabled || false}
                  onCheckedChange={(enabled) => updateItem({ actions_enabled: enabled })}
                  data-testid="switch-actions-enabled"
                />
              </div>
              {item.actions_enabled && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 bg-amber-50 dark:bg-amber-950/20 p-2 rounded">
                  ⚠️ When enabled, responses to this item will require manager review and action completion before the audit can be approved.
                </p>
              )}
            </div>
          </>
        )}

        <Separator />

        {/* Scoring Configuration */}
        <div>
          <h4 className="font-medium mb-3">Scoring Configuration</h4>
          {renderScoringEditor()}
        </div>

        {/* Advanced Settings */}
        {showAdvanced && (
          <>
            <Separator />
            <div>
              <h4 className="font-medium mb-3">Advanced Settings</h4>
              <div className="space-y-3">
                <div>
                  <Label>Item ID</Label>
                  <Input
                    value={item.id}
                    onChange={(e) => updateItem({ id: e.target.value })}
                    className="mt-1 font-mono text-sm"
                    data-testid="input-item-id"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Unique identifier for this item. Use caution when changing.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}